import { useCallback, useRef, useSyncExternalStore } from 'react'
import isEqual from 'lodash/isEqual'

import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import ComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import { draftMediaToAssets } from './mapDraftToOmni'
import { projectFluxThreadItems } from './projectFluxChannelContent'
import type { LegacyChannelData } from './types'

const EMPTY_LEGACY_CHANNEL_DATA: LegacyChannelData = Object.freeze({
  service: null,
  text: '',
  urls: [],
  unshortenedUrls: [],
  assets: [],
  aiAssisted: false,
  channelData: null,
  updateType: null,
  isReminder: false,
  title: null,
  enabledAttachmentType: null,
  sourceLink: null,
  retweet: null,
  selectedStickers: [],
  places: null,
  document: null,
  threadItems: [],
  isOmniMode: false,
})

function buildSnapshot(channelId: string): LegacyChannelData {
  const profile = AppStore.getProfile(channelId)
  if (!profile) return EMPTY_LEGACY_CHANNEL_DATA

  const serviceDraft = ComposerStore.getDraft(profile.service.name)
  if (!serviceDraft) return EMPTY_LEGACY_CHANNEL_DATA

  // While omni mode is active (AppStore.getIsOmniboxEnabled() === true),
  // keystrokes land on the 'omni' draft; per-service drafts only sync when the
  // user exits omni mode via "Customize for each network". Content therefore
  // comes from the omni draft in that mode, while channel-specific fields
  // (channelData, updateType, title, enabledAttachmentType, sourceLink,
  // retweet, selectedStickers, places, isReminder, service) always come from
  // the per-service draft — the omni draft holds none of them.
  const isOmniMode = AppStore.getIsOmniboxEnabled() === true
  const omniDraft = isOmniMode ? ComposerStore.getDraft('omni') : null
  const contentDraft = omniDraft ?? serviceDraft

  return {
    service: serviceDraft.service,
    text: contentDraft.text,
    urls: [...contentDraft.urls],
    unshortenedUrls: [...contentDraft.unshortenedUrls],
    assets: draftMediaToAssets(
      {
        link: contentDraft.link,
        images: contentDraft.images,
        videos: contentDraft.videos,
        gif: contentDraft.gif,
        document: contentDraft.document,
      },
      { hasLinkAttachment: contentDraft.hasLinkAttachmentEnabled() },
    ),
    aiAssisted: contentDraft.aiAssisted,
    channelData: serviceDraft.channelData,
    updateType: serviceDraft.updateType,
    isReminder: serviceDraft.isReminder ?? false,
    title: serviceDraft.title,
    enabledAttachmentType: serviceDraft.enabledAttachmentType,
    sourceLink: serviceDraft.sourceLink,
    retweet: serviceDraft.retweet,
    selectedStickers: serviceDraft.selectedStickers,
    places: serviceDraft.places,
    document: contentDraft.document,
    // Threads live on the per-service draft in both modes (the omni draft has
    // none), same as the other channel-specific fields above.
    threadItems: projectFluxThreadItems(serviceDraft),
    isOmniMode,
  }
}

/**
 * Subscribe to legacy Flux ComposerStore changes for a single channel and
 * return a plain snapshot of its draft. Uses useSyncExternalStore for
 * tear-free reads.
 *
 * While omni mode is active the content fields reflect the shared 'omni'
 * draft (the per-service draft's content is stale until omni mode exits);
 * channel-specific fields always come from the per-service draft.
 * `isOmniMode` exposes which case applied, for callers that need to pick a
 * content source themselves (e.g. per-channel Zustand reads, which must not
 * apply while the per-service draft is stale).
 *
 * Invariant: snapshotRef.current changes identity only when snapshot content
 * actually changes. This means React's post-subscribe getSnapshot check detects
 * real content changes (closing the render→subscribe gap), and unrelated Flux
 * emissions that don't affect this channel's data do not re-render consumers.
 */
export function useLegacyChannelData(channelId: string): LegacyChannelData {
  const snapshotRef = useRef<LegacyChannelData>(buildSnapshot(channelId))

  // When channelId changes, update the ref immediately so getSnapshot returns
  // the right data before the next store event fires.
  const channelIdRef = useRef(channelId)
  if (channelIdRef.current !== channelId) {
    channelIdRef.current = channelId
    snapshotRef.current = buildSnapshot(channelId)
  }

  // Stable identity: a new subscribe function would make useSyncExternalStore
  // tear down and re-create the store subscriptions on every render.
  const subscribe = useCallback((onStoreChange: () => void): (() => void) => {
    const handleChange = (): void => {
      const next = buildSnapshot(channelIdRef.current)
      // Only swap the ref when content actually changed. This prevents
      // unrelated Flux emissions (which fire on every store mutation, not
      // per-channel) from producing new references and re-rendering consumers.
      if (!isEqual(next, snapshotRef.current)) {
        snapshotRef.current = next
      }
      onStoreChange()
    }

    AppStore.addChangeListener(handleChange)
    ComposerStore.addChangeListener(handleChange)

    // Synchronously re-check after subscribing to catch any store change that
    // landed between the render that computed snapshotRef and this subscription.
    // React re-reads getSnapshot right after subscribe() returns; swapping the
    // ref here (when content differs) makes that check effective.
    const afterSubscribe = buildSnapshot(channelIdRef.current)
    if (!isEqual(afterSubscribe, snapshotRef.current)) {
      snapshotRef.current = afterSubscribe
    }

    return (): void => {
      AppStore.removeChangeListener(handleChange)
      ComposerStore.removeChangeListener(handleChange)
    }
  }, [])

  const getSnapshot = (): LegacyChannelData => snapshotRef.current

  return useSyncExternalStore(subscribe, getSnapshot)
}
